// ==========================================
// 📅 СОБЫТИЯ - ЕДИНЫЕ ТИПЫ
// ==========================================
// Типы для событий на основе Event Master Backend API v1.5

import {
  IdentityFields,
  TimestampFields,
  Location,
  PaginatedResponse,
  BaseFilters,
} from "./unified-types";

// Единственный источник истины для статусов событий (совпадает с backend EventStatus)
export enum EventStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  PUBLISHED = "published",
  ONGOING = "ongoing",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  POSTPONED = "postponed",
}

// === ОСНОВНОЙ ИНТЕРФЕЙС СОБЫТИЯ ===

export interface Event extends IdentityFields, TimestampFields {
  // Основная информация
  title: string;
  description: string;
  type: EventType;
  format: EventFormat;
  status: EventStatus;

  // Время и место
  startDate: Date;
  endDate: Date;
  /**
   * AD-527. Пояс события, имя IANA (`events.timezone`); `null` — не известен
   * (события до AD-527). По нему считаются дни, в которых допустим пункт
   * программы.
   */
  timezone: string | null;
  location?: EventLocation | null;

  // Участники
  organizerId: string;
  organizerName?: string; // Добавляем для совместимости
  // ADR-EVENTS-DTO (F2.1 шаг 3.3): инлайн-массив участников убран из read-DTO.
  // Источник списка — пагинированный GET /events/:eventId/participants (useEventParticipants);
  // счётчик — currentParticipants / useEventStats. См. ADR §2,§5.
  maxParticipants?: number;
  /**
   * №245 (решение Сергея 26.10.2026). Сколько мест даст запись: меньшее из
   * `maxParticipants` и купленных мест, считает сервер; сами купленные места
   * посторонним не отдаются. Поля нет — ответ бэкенда старше №245 (TD-31(б)).
   * Читать через `utils/events/bookable-capacity.ts`, а не напрямую.
   */
  effectiveCapacity?: number;
  currentParticipants?: number; // Добавляем для совместимости
  registrationDeadline?: Date;

  // Контент
  agenda?: EventAgenda;
  budget?: EventBudget;
  tags: string[];
  category: string;
  /**
   * Категории события. Отдаются бэкендом (`events.categories`, simple-array) и
   * до Ц6 в этом типе отсутствовали — экран правки не мог их прочитать и
   * отправлял вместо них категорию выбранного типа события (AD-375).
   */
  categories?: string[];
  /**
   * Сегменты целевой аудитории (`events.targetAudience`, simple-array). Как и
   * `categories`, приезжают с события, но в типе их не было — форма правки
   * начинала с пустого списка и стирала их при сохранении (AD-374).
   */
  targetAudience?: string[];

  // Настройки
  // Н-6: `requiresApproval` и `allowGuestRegistration` были объявлены
  // ОБЯЗАТЕЛЬНЫМИ полями события, хотя бэкенд их не отдаёт и не знает вовсе —
  // значение бралось из умолчаний нормализатора. Сняты вместе с write-половиной.
  visibility: EventVisibility;

  // Медиа
  coverImage?: string;
  images: string[];
  documents: EventDocument[];

  // Цена и валюта
  price?: number; // Добавляем для совместимости
  currency?: string; // Добавляем для совместимости

  // Статистика и рейтинг
  stats?: EventStats;
  rating?: number; // Рейтинг события (0-5)
  views?: number; // Добавляем для совместимости
}

// === ТИПЫ И ЭНУМЫ ===

export enum EventType {
  // Corporate
  MEETING = "meeting",
  CONFERENCE = "conference",
  WORKSHOP = "workshop",
  WEBINAR = "webinar",
  TEAM_BUILDING = "team_building",
  TRAINING = "training",
  PRESENTATION = "presentation",
  BOARD_MEETING = "board_meeting",
  COMPANY_RETREAT = "company_retreat",
  NETWORKING = "networking",
  PRODUCT_LAUNCH = "product_launch",
  AWARD_CEREMONY = "award_ceremony",
  // Educational
  SEMINAR = "seminar",
  LECTURE = "lecture",
  MASTERCLASS = "masterclass",
  BOOTCAMP = "bootcamp",
  CERTIFICATION = "certification",
  EXAMINATION = "examination",
  // Entertainment
  PARTY = "party",
  FESTIVAL = "festival",
  CONCERT = "concert",
  EXHIBITION = "exhibition",
  THEATER = "theater",
  SPORTS_EVENT = "sports_event",
  GAME_TOURNAMENT = "game_tournament",
  // Social
  FUNDRAISER = "fundraiser",
  CHARITY_EVENT = "charity_event",
  VOLUNTEER = "volunteer",
  COMMUNITY_MEETING = "community_meeting",
  SUPPORT_GROUP = "support_group",
  // Personal
  BIRTHDAY = "birthday",
  ANNIVERSARY = "anniversary",
  WEDDING = "wedding",
  GRADUATION = "graduation",
  BABY_SHOWER = "baby_shower",
  FAMILY_REUNION = "family_reunion",

  OTHER = "other",
}

export enum EventFormat {
  ONLINE = "online",
  OFFLINE = "offline",
  HYBRID = "hybrid",
}

/**
 * AD-389 — СПОСОБ УЧАСТИЯ, ЗАЯВЛЕННЫЙ САМИМ ЧЕЛОВЕКОМ на гибридном событии.
 * Зеркало BE `ParticipationMode` (`shared-types.ts`).
 *
 * ЭТО НАМЕРЕНИЕ, А НЕ ПРАВО. Выбравший `OFFLINE` всё равно получает ссылку на
 * онлайн-часть: планы меняются, а закрытая дверь стоила бы человеку события.
 * На доступ значение не влияет НИГДЕ — ни здесь, ни на сервере.
 *
 * СОСТОЯНИЙ ТРИ, А НЕ ДВА. Отсутствие значения (`null`) — это «не заявлен», и
 * оно не равно «придёт очно»: так выглядят участники негибридных событий (там
 * способ выражен форматом), те, кого завёл не он сам (приглашение, организатор),
 * и все, кто записался до появления поля. Экран, показывающий два состояния
 * вместо трёх, превратит «не знаем» в «очно» и испортит ровно то число, ради
 * которого поле заведено.
 *
 * Почему `enum`, а не строковый союз, как у `OnlineAccessDenial`: то — словарь
 * ПРИЧИН, который экран только читает, а этот значение экран ещё и ОТПРАВЛЯЕТ.
 * Рядом с `EventFormat` и `ParticipantStatus` он живёт той же формой, что и
 * соседи, и опечатка в отправляемом значении становится ошибкой компилятора.
 */
export enum ParticipationMode {
  OFFLINE = "offline",
  ONLINE = "online",
}

export enum EventVisibility {
  PUBLIC = "public",
  PRIVATE = "private",
  INTERNAL = "internal",
}

export type EventPriority = "low" | "medium" | "high" | "critical";

// === МЕСТОПОЛОЖЕНИЕ ===

export interface EventLocation extends Location {
  name: string;
  capacity?: number;
  facilities: string[];
  onlineDetails?: {
    platform: string;
    meetingId?: string;
    password?: string;
    link: string;
  };

  // Метод для отображения в UI
  toString(): string;
}

// === УЧАСТНИКИ ===

export interface EventParticipant extends IdentityFields, TimestampFields {
  eventId: string;
  userId: string;
  email: string;
  name: string;
  fullName: string;
  role: ParticipantRole;
  status: ParticipantStatus;

  // Дополнительная информация
  registeredAt: Date;
  checkedInAt?: Date;
  checkedOutAt?: Date;

  /** Код регистрации (для QR-чек-ина по коду, ADR-EVENTS-DTO F2.1 шаг 3.2). */
  registrationCode?: string;

  // Персонализация
  dietary?: string[];
  specialRequests?: string;
  emergencyContact?: string;

  // Метаданные
  source: string; // откуда пришла регистрация
  metadata?: Record<string, any>;

  // Алиасы для обратной совместимости
  userName?: string; // Алиас для name
  userEmail?: string; // Алиас для email
  registrationDate?: string; // Алиас для registeredAt
  attendedAt?: string; // Алиас для checkedInAt
}

// Совпадает с backend ParticipantRole
export enum ParticipantRole {
  ATTENDEE = "attendee",
  SPEAKER = "speaker",
  MODERATOR = "moderator",
  ORGANIZER = "organizer",
  CO_ORGANIZER = "co_organizer",
  VOLUNTEER = "volunteer",
  SPONSOR = "sponsor",
  MEDIA = "media",
}

// Зеркало backend `ParticipantStatus` (`src/shared-types.ts`). Значения обязаны
// совпадать с типом колонки `event_participants_status_enum`, а не с именами
// членов: `CANCELED` — это строка `cancelled` с ДВУМЯ 'l', как в колонке. Прежний
// комментарий здесь узаконивал расхождение в одну букву — из-за него сравнения
// `status === ParticipantStatus.CANCELED` были ложны всегда, и экраны молча врали
// (O-22). Все девять значений ниже физическая колонка ЗНАЕТ: словарь
// `event_participants_status_enum` замерен 06.09.2026 на обеих базах
// (`eventmaster` и `eventos_migration_check`) и несёт ровно эти девять меток —
// фаза 2 O-22 закрыта. Прежняя оговорка «пять значений колонка не знает»
// устарела, и на неё опирался укороченный список фильтра статусов — AD-98.
export enum ParticipantStatus {
  REGISTERED = "registered",
  CONFIRMED = "confirmed",
  CANCELED = "cancelled",
  ATTENDED = "attended",
  WAITLISTED = "waitlisted",
  INVITED = "invited",
  PENDING = "pending",
  REJECTED = "rejected",
  NO_SHOW = "no_show",
}

// === ПОВЕСТКА ===

export interface EventAgenda extends IdentityFields, TimestampFields {
  eventId: string;
  items: EventAgendaItem[];
  timezone: string;
}

export interface EventAgendaItem extends IdentityFields, TimestampFields {
  agendaId: string;
  name: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  location?: string;
  speakers: AgendaSpeaker[];
  /**
   * AD-499. Ведущий пункта ОДНОЙ строкой — поле `speaker` бэкендового
   * `AgendaItemDto`, то есть состав колонки `events.agenda` (jsonb). Это не
   * замена `speakers`: богатый список принадлежит другому хранилищу повестки
   * (связь `agendaItems`, отдельные двери `/events/:id/agenda`), а в jsonb
   * поля под него нет. Без этой строки круг «открыли правку → сохранили»
   * обнулял ведущего у каждого пункта.
   */
  speaker?: string;
  type: AgendaItemType;
  isBreak: boolean;
  materials?: AgendaMaterial[];
}

export interface AgendaSpeaker {
  id: string;
  name: string;
  title?: string;
  company?: string;
  bio?: string;
  avatar?: string;
  socialLinks?: Record<string, string>;
}

export interface AgendaMaterial {
  id: string;
  name: string;
  type: MaterialType;
  url: string;
  size?: number;
  description?: string;
}

export enum AgendaItemType {
  SESSION = "session",
  PRESENTATION = "presentation",
  OPENING = "opening",
  BREAK = "break",
  WORKSHOP = "workshop",
  KEYNOTE = "keynote",
  PANEL = "panel",
  NETWORKING = "networking",
  LUNCH = "lunch",
  COFFEE = "coffee",
  REGISTRATION = "registration",
  QA = "qa",
  CLOSING = "closing",
  OTHER = "other",
}

export enum MaterialType {
  PRESENTATION = "presentation",
  DOCUMENT = "document",
  VIDEO = "video",
  AUDIO = "audio",
  LINK = "link",
  OTHER = "other",
}

// === БЮДЖЕТ ===

export interface EventBudget extends IdentityFields, TimestampFields {
  eventId: string;
  totalBudget: number;
  currency: string;
  items: EventBudgetItem[];
  actualSpent: number;
  spent: number; // Алиас для совместимости
  status: BudgetStatus;
}

export interface EventBudgetItem
  extends Omit<IdentityFields, "name">,
    TimestampFields {
  budgetId?: string;
  category: BudgetCategory | string;
  name?: string; // optional: API может присылать только title
  title: string; // Алиас для совместимости
  plannedAmount: number;
  actualAmount: number;
  amount?: number; // Алиас для совместимости
  /** Тип статьи: income | expense (приходит с бэкенда Events API). */
  type?: string;
  supplierId?: string;
  status: BudgetItemStatus | string;
  notes?: string;
  description?: string;
  /** Дата срока оплаты (бэкенд Events API). */
  dueDate?: string | Date;
  /** Дата фактической оплаты (бэкенд Events API). */
  paymentDate?: string | Date;
}

export enum BudgetCategory {
  VENUE = "venue",
  CATERING = "catering",
  SPEAKERS = "speakers",
  MARKETING = "marketing",
  EQUIPMENT = "equipment",
  MATERIALS = "materials",
  TRANSPORT = "transport",
  ACCOMMODATION = "accommodation",
  ENTERTAINMENT = "entertainment",
  STAFF = "staff",
  OTHER = "other",
}

export enum BudgetStatus {
  DRAFT = "draft",
  PLANNED = "planned",
  APPROVED = "approved",
  IN_PROGRESS = "in_progress",
  CONFIRMED = "confirmed",
  COMPLETED = "completed",
  PAID = "paid",
  CANCELLED = "cancelled",
  OVER_BUDGET = "over_budget",
}

export enum BudgetItemStatus {
  PLANNED = "planned",
  APPROVED = "approved",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

// === ДОКУМЕНТЫ ===

export interface EventDocument extends IdentityFields, TimestampFields {
  eventId: string;
  name: string;
  type: EventDocumentType;
  url: string;
  size: number;
  uploadedBy: string;
  isPublic: boolean;
  category: DocumentCategory;
}

export enum EventDocumentType {
  PDF = "pdf",
  DOC = "doc",
  DOCX = "docx",
  XLS = "xls",
  XLSX = "xlsx",
  PPT = "ppt",
  PPTX = "pptx",
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
  OTHER = "other",
}

export enum DocumentCategory {
  AGENDA = "agenda",
  PRESENTATION = "presentation",
  MATERIAL = "material",
  CONTRACT = "contract",
  INVOICE = "invoice",
  REPORT = "report",
  CERTIFICATE = "certificate",
  OTHER = "other",
}

// === СТАТИСТИКА ===

export interface EventStats {
  // Основная статистика
  totalEvents: number;
  activeEvents: number;
  completedEvents: number;
  draftEvents: number;
  cancelledEvents: number;
  totalParticipants: number;
  averageParticipants: number;
  totalRevenue: number;

  // Дополнительные поля для совместимости
  publishedEvents: number;
  upcomingEvents: number;
  pastEvents: number;

  // Регистрации
  totalRegistrations: number;
  confirmedAttendees: number;
  actualAttendees: number;
  noShows: number;
  cancellations: number;

  // Демография
  demographics: {
    ageGroups: Record<string, number>;
    genders: Record<string, number>;
    locations: Record<string, number>;
  };

  // Активность
  engagement: {
    avgSessionDuration: number;
    peakAttendance: number;
    interactionRate: number;
  };

  // Обратная связь
  feedback: {
    avgRating: number;
    totalResponses: number;
    nps: number;
  };
}

// === ФИЛЬТРЫ И ПОИСК ===

export interface FilterEventsParams extends BaseFilters {
  type?: EventType[];
  format?: EventFormat[];
  startDate?: Date;
  endDate?: Date;
  location?: string;
  organizerId?: string;
  tags?: string[];
  category?: string;
  minParticipants?: number;
  maxParticipants?: number;
}

// === API TYPES ===

export type EventsResponse = PaginatedResponse<Event>;
export type EventParticipantsResponse = PaginatedResponse<EventParticipant>;
export type EventStatsResponse = EventStats;

// === УТИЛИТАРНЫЕ ТИПЫ ===

export type EventSummary = Pick<
  Event,
  "id" | "title" | "type" | "startDate" | "endDate" | "status" | "coverImage"
>;

export type EventWithStats = Event & {
  stats: EventStats;
};

// === КОНСТАНТЫ ===

export const EVENT_DEFAULTS = {
  timezone: "UTC",
  // EUR-5: было "RUB" — расходилось с функциональной валютой платформы
  // (ADR-MONEY §7). Ссылок на константу сейчас нет, но именно поэтому её и
  // правим: она сработает молча в первом же месте, где ею воспользуются.
  currency: "EUR",
  maxParticipants: 100,
} as const;

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  // Corporate
  [EventType.MEETING]: "Встреча",
  [EventType.CONFERENCE]: "Конференция",
  [EventType.WORKSHOP]: "Мастер-класс",
  [EventType.WEBINAR]: "Вебинар",
  [EventType.TEAM_BUILDING]: "Тимбилдинг",
  [EventType.TRAINING]: "Тренинг",
  [EventType.PRESENTATION]: "Презентация",
  [EventType.BOARD_MEETING]: "Совет директоров",
  [EventType.COMPANY_RETREAT]: "Корпоратив",
  [EventType.NETWORKING]: "Нетворкинг",
  [EventType.PRODUCT_LAUNCH]: "Запуск продукта",
  [EventType.AWARD_CEREMONY]: "Церемония награждения",
  // Educational
  [EventType.SEMINAR]: "Семинар",
  [EventType.LECTURE]: "Лекция",
  [EventType.MASTERCLASS]: "Мастер-класс (углублённый)",
  [EventType.BOOTCAMP]: "Буткемп",
  [EventType.CERTIFICATION]: "Сертификация",
  [EventType.EXAMINATION]: "Экзамен",
  // Entertainment
  [EventType.PARTY]: "Вечеринка",
  [EventType.FESTIVAL]: "Фестиваль",
  [EventType.CONCERT]: "Концерт",
  [EventType.EXHIBITION]: "Выставка",
  [EventType.THEATER]: "Театр",
  [EventType.SPORTS_EVENT]: "Спортивное мероприятие",
  [EventType.GAME_TOURNAMENT]: "Игровой турнир",
  // Social
  [EventType.FUNDRAISER]: "Сбор средств",
  [EventType.CHARITY_EVENT]: "Благотворительность",
  [EventType.VOLUNTEER]: "Волонтёрство",
  [EventType.COMMUNITY_MEETING]: "Собрание сообщества",
  [EventType.SUPPORT_GROUP]: "Группа поддержки",
  // Personal
  [EventType.BIRTHDAY]: "День рождения",
  [EventType.ANNIVERSARY]: "Юбилей",
  [EventType.WEDDING]: "Свадьба",
  [EventType.GRADUATION]: "Выпускной",
  [EventType.BABY_SHOWER]: "Встреча малыша",
  [EventType.FAMILY_REUNION]: "Семейная встреча",

  [EventType.OTHER]: "Другое",
} as const;

// Константа для дефолтной локации
export const DEFAULT_EVENT_LOCATION: EventLocation = {
  name: "",
  address: "",
  city: "",
  country: "",
  facilities: [],
  coordinates: { lat: 0, lng: 0 },
  toString() {
    return this.name || this.address || "Место не указано";
  },
};

// === ТИПЫ ДЛЯ API ===

// Создание события
export interface CreateEventData {
  title: string;
  description?: string;
  type: EventType;
  format: EventFormat;
  startDate: Date;
  endDate: Date;
  timezone: string;
  location: EventLocation;
  isPublic: boolean;
  maxParticipants?: number;
  tags?: string[];
  coverImage?: string;
  registrationDeadline?: Date;
  requiresApproval?: boolean;
  allowGuestRegistration?: boolean;
}

// Обновление события
export interface UpdateEventData extends Partial<CreateEventData> {
  status?: EventStatus;
}

// Параметры фильтрации событий
export interface EventFilterParams extends Omit<BaseFilters, "status"> {
  type?: EventType | EventType[];
  format?: EventFormat | EventFormat[];
  status?: EventStatus | EventStatus[];
  startDate?: Date;
  endDate?: Date;
  location?: string;
  organizerId?: string;
  tags?: string[];
  category?: string;
  minParticipants?: number;
  maxParticipants?: number;
}

// === ТИПЫ ЗАДАЧ СОБЫТИЙ ===

// Вокабуляр BE-канона (Вариант A, owner 2026-07-24): FE перенял значения enum'ов
// BE `TaskStatus`/`TaskPriority` (src/shared/entities/tasks/task.entity.ts) один-в-один.
// `deadline` — реальное поле entity (НЕ `dueDate`); `isPersonal` — производное от
// `taskType` на BE, в теле запроса НЕ передаётся (whitelist `forbidNonWhitelisted`).
export type EventTaskStatus =
  | "todo"
  | "in_progress"
  | "review"
  | "blocked"
  | "completed"
  | "cancelled";
export type EventTaskPriority = "low" | "medium" | "high" | "urgent";
// Один список на тип и на обе формы создания задачи; порядок — порядок в списке выбора.
export const TASK_TYPE_VALUES = [
  "personal",
  "organizational",
  "administrative",
] as const;
export type TaskTypeValue = (typeof TASK_TYPE_VALUES)[number];

/**
 * AD-646. Человек задачи на проводе — вложенная учётка (`task.creator`,
 * `task.assignee`). Плоских `creatorName` / `assigneeName` сервер не отдаёт и не
 * отдавал: поля с такими именами стояли здесь без источника, и экраны, читавшие
 * их, падали. Имя собирает `taskPersonName` (`utils/task-display.ts`).
 */
export interface EventTaskPerson {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
}

export interface EventTask extends IdentityFields, TimestampFields {
  eventId: string;
  title: string;
  description: string;
  status: EventTaskStatus;
  priority: EventTaskPriority;
  assigneeId?: string;
  // Учётки читаются с проверкой наличия (TD-31): не каждый маршрут их подгружает.
  assignee?: EventTaskPerson | null;
  creatorId: string;
  creator?: EventTaskPerson | null;
  deadline?: Date;
  tags?: string[];
  estimatedHours?: number;
  actualHours?: number;
  progress: number; // 0-100
  taskType: TaskTypeValue;
  isPersonal: boolean;
}

export interface CreateEventTaskRequest {
  title: string;
  description: string;
  priority: EventTaskPriority;
  status?: EventTaskStatus;
  assigneeId?: string;
  deadline?: Date;
  tags?: string[];
  estimatedHours?: number;
  actualHours?: number;
  taskType: TaskTypeValue;
}

/**
 * Тело `PUT /events/:eventId/tasks/:taskId`. `null` у исполнителя, срока и часов —
 * «снять значение»: сервер пишет его в колонку (AD-659, решение Сергея №146);
 * пропуск поля значение не трогает.
 */
export interface UpdateEventTaskRequest {
  title?: string;
  description?: string;
  status?: EventTaskStatus;
  priority?: EventTaskPriority;
  assigneeId?: string | null;
  deadline?: Date | null;
  tags?: string[];
  estimatedHours?: number | null;
  actualHours?: number | null;
  progress?: number;
  taskType?: TaskTypeValue;
}

export interface TaskFilters extends BaseFilters {
  status?: string;
  priority?: string;
  assigneeId?: string;
  creatorId?: string;
  taskType?: "personal" | "organizational" | "administrative";
  isPersonal?: boolean;
  page?: number;
  limit?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar: GET /calendar/user/:userId/events
// Backend contract: calendar.controller.ts
// ─────────────────────────────────────────────────────────────────────────────

export interface UserCalendarEvent {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: EventStatus;
  type: EventType;
  location?: string;
  role?: ParticipantRole;
}

export interface UserCalendarEventsParams {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export type UserCalendarEventsResponse = PaginatedResponse<UserCalendarEvent>;
